import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Chip, CircularProgress, FormControlLabel, Slider, Stack, Switch, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SecurityRoundedIcon from '@mui/icons-material/SecurityRounded';
import BalanceRoundedIcon from '@mui/icons-material/BalanceRounded';
import LocalFireDepartmentRoundedIcon from '@mui/icons-material/LocalFireDepartmentRounded';
import { UserApi } from '../../api/UserApi';
import { AICheckerState } from '../../interface/AICheckerState';
import { ApplicationState } from '../../interface/ApplicationState';
import { BotExchange } from '../../interface/BotExchange';
import { updateLocalUser } from '../../redux/actions/userActions';

export const supportsAI = (exchange?: BotExchange) =>
  exchange === BotExchange.binance || exchange === BotExchange.binanceUS || exchange === BotExchange.binanceTestnet;

const statuses: Record<string, string> = {
  running: 'Reviewing markets', decided: 'Decision ready', hold: 'Holding', executed: 'Trade completed',
  error: 'Check failed', submitting: 'Submitting order', order_unknown: 'Order needs review',
};
const actions: Record<string, string> = { BUY: 'Buy', TAKE_PROFIT: 'Take profit', STOP_LOSS: 'Stop loss', HOLD: 'Hold' };
const dateLabel = (date?: string) => date && !Number.isNaN(Date.parse(date)) ? new Date(date).toLocaleString() : '—';
const profiles = [
  { value: 'conservative', label: 'Conservative', detail: 'Fewer trades · stronger confirmation', icon: SecurityRoundedIcon, color: '#4f8df7' },
  { value: 'balanced', label: 'Balanced', detail: 'A measured mix of confirmation and opportunity', icon: BalanceRoundedIcon, color: '#9c6ade' },
  { value: 'yolo', label: 'Yolo', detail: 'More trigger-happy · accepts more risk', icon: LocalFireDepartmentRoundedIcon, color: '#f08c46' },
] as const;

export const AICheckerSettings = () => {
  const dispatch = useDispatch();
  const user = useSelector((state: ApplicationState) => state.userStore.user);
  const profileLoading = useSelector((state: ApplicationState) => state.userStore.isGettingUserProfile);
  const profileSaving = useSelector((state: ApplicationState) => state.userStore.isSettingUserProfile);
  const [state, setState] = useState<AICheckerState | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [refresh, setRefresh] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    if (!user._id) return;
    let active = true;
    let pending = false;
    const id = ++requestId.current;
    const load = async () => {
      if (pending) return;
      pending = true;
      setLoading(true);
      try {
        const result = await new UserApi().getAIState();
        if (result === undefined) throw new Error('No response');
        if (active && id === requestId.current) { setState(result); setError(''); }
      } catch {
        if (active && id === requestId.current) setError('Unable to load AIChecker status. Please refresh to try again.');
      } finally {
        pending = false;
        if (active && id === requestId.current) setLoading(false);
      }
    };
    void load();
    const timer = window.setInterval(() => { void load(); }, 60000);
    return () => { active = false; window.clearInterval(timer); };
  }, [user._id, refresh, profileSaving]);

  useEffect(() => { setState(null); setError(''); }, [user._id]);
  const supported = supportsAI(user.botExchange);
  const uncertain = state?.status === 'order_unknown' || state?.status === 'submitting';
  const profile = user.aiCheckerProfile || 'balanced';
  const profileIndex = Math.max(0, profiles.findIndex(item => item.value === profile));
  const ActiveProfileIcon = profiles[profileIndex].icon;
  const profileMarks = profiles.map((_, index) => ({ value: index }));

  return <Stack spacing={2} >
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={1}>
      <FormControlLabel label="Enable AIChecker" control={<Switch checked={!!user.isAIEnabled}
        disabled={profileLoading || profileSaving || (!supported && !user.isAIEnabled)}
        onChange={event => dispatch(updateLocalUser({ isAIEnabled: event.target.checked }))} />} />
      <Chip label="At most once per hour" variant="outlined" />
    </Stack>
    <Typography variant="body2" color="text.secondary">
      AIChecker reviews your holdings, technical indicators, signals and news to buy, take profit, stop loss or hold.
      It discovers Binance spot markets using your funding currency and can trade coins outside your saved pairs.
    </Typography>
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2, bgcolor: 'action.hover' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>AI trading style</Typography>
          <Typography variant="body2" color="text.secondary">Choose how much confirmation the AI needs before it acts.</Typography>
        </Box>
        <Chip color="primary" icon={<ActiveProfileIcon />} label={profiles[profileIndex].label} />
      </Stack>
      <Box sx={{ px: { xs: 0.5, sm: 1 }, pt: 3, pb: 5, overflow: 'visible' }}>
        <Slider aria-label="AI trading style" min={0} max={2} step={1} marks={profileMarks} value={profileIndex}
          onChange={(_, value) => {
            const next = profiles[Number(value)].value;
            dispatch(updateLocalUser({ aiCheckerProfile: next }));
          }} sx={{ '& .MuiSlider-rail': { opacity: 1, background: 'linear-gradient(90deg, #4f8df7 0%, #9c6ade 50%, #f08c46 100%)' }, '& .MuiSlider-track': { background: 'transparent', border: 'none' }, '& .MuiSlider-thumb': { boxShadow: '0 0 0 6px rgba(156, 106, 222, 0.16)' }, '& .MuiSlider-mark': { width: 8, height: 8, borderRadius: '50%', backgroundColor: 'background.paper', border: '2px solid currentColor' } }} />
      </Box>
      <Stack direction="row" sx={{ mt: -2, px: { xs: 0.5, sm: 1 } }}>
        {profiles.map((item, index) => {
          const ProfileIcon = item.icon;
          return <Stack key={item.value} alignItems={index === 0 ? 'flex-start' : index === profiles.length - 1 ? 'flex-end' : 'center'} sx={{ width: '33.333%', minWidth: 0, color: item.color }}>
            <ProfileIcon sx={{ fontSize: 19 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.15 }}>{item.label}</Typography>
          </Stack>;
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{profiles[profileIndex].detail}. The backend keeps the actual thresholds fixed for safety.</Typography>
    </Box>
    <Alert severity="info">
      AI controls entry and exit timing, including when to take a loss. Your configured indicators, pair list,
      stop-loss and take-profit rules are bypassed. Maximum buy and sell amounts still apply.
      Decisions can place one market order per hour. Save your settings to apply changes.
    </Alert>
    {!supported && <Alert severity="warning">AIChecker supports Binance, Binance.US and Binance TestNet. Select one of these exchanges or disable AIChecker before saving an active trading configuration.</Alert>}
    {user.isAIEnabled && !user.tradingEnabled && <Alert severity="info">Trading is paused. Enable Trading in Trading Settings and save to allow AIChecker to trade.</Alert>}
    <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={600}>Latest saved activity</Typography>
        <Button size="small" startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={loading || !user._id} onClick={() => setRefresh(value => value + 1)}>Refresh status</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading && !state && <Typography role="status" variant="body2">Loading AIChecker activity…</Typography>}
      {!loading && !state && !error && <Typography variant="body2" color="text.secondary">No AIChecker activity yet. After enabling and saving, the bot will check when it next runs.</Typography>}
      {state && <Stack spacing={1.5} sx={{ opacity: error ? 0.65 : 1 }}>
        {error && <Typography variant="caption">Showing previously loaded activity.</Typography>}
        <Chip sx={{ alignSelf: 'flex-start' }} label={statuses[state.status] || 'Unknown status'}
          color={uncertain || state.status === 'error' ? 'warning' : state.status === 'executed' ? 'success' : 'default'} />
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 0.5, md: 3 }}>
          <Typography variant="body2">Last check: {dateLabel(state.lastAttemptAt)}</Typography>
          <Typography variant="body2">Next eligible check: {dateLabel(state.nextRunAt)}</Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">Checks require saved AI settings and trading to be enabled. Status refreshes every minute.</Typography>
        {state.decision && <Box>
          <Typography fontWeight={600}>
            {actions[state.decision.action] || state.decision.action}
            {state.decision.symbol ? ` · ${state.decision.symbol}` : ''}
          </Typography>
          {state.decision.action !== 'HOLD' && <Typography variant="body2">
            Requested amount: {state.decision.quoteAmount} in the selected market’s quote currency
          </Typography>}
          <Typography variant="body2" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>{state.decision.reason}</Typography>
        </Box>}
        {state.error && <Alert severity="warning">{state.error}</Alert>}
        {uncertain && <Alert severity="warning">Further AI trades are blocked while this order is being submitted or needs reconciliation. If this status persists, contact support to review the exchange order. Switching AI off and on does not reset it.</Alert>}
      </Stack>}
    </Box>
  </Stack>;
};
